/** Ported from Anvil src/lib/zone-kinds.ts (pure data + helpers). */
import type { Zone, ZoneKind, ScreenSurface } from '@volley/crucible-ipc-types';

export const ZONE_KINDS: { value: ZoneKind; label: string; color: string }[] = [
  { value: 'video', label: 'Video', color: '#4f8aff' },
  { value: 'host', label: 'Host', color: '#ff6ab0' },
  { value: 'score', label: 'Score', color: '#ffc54a' },
  { value: 'timer', label: 'Timer', color: '#ff9a45' },
  { value: 'list', label: 'List', color: '#9d6cff' },
  { value: 'button', label: 'Button', color: '#5fd084' },
  { value: 'text', label: 'Text', color: '#9aa3b0' },
  { value: 'controller-input', label: 'Controller', color: '#3fc7c2' },
  { value: 'art', label: 'Art / Image', color: '#7d77ff' },
  { value: 'other', label: 'Other', color: '#777783' },
];

export function colorFor(kind: string): string {
  return ZONE_KINDS.find((k) => k.value === kind)?.color ?? '#777783';
}

export function labelFor(kind: string): string {
  return ZONE_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

export const SURFACE_DIMENSIONS: Record<
  'tv' | 'phone',
  { width: number; height: number; label: string }
> = {
  tv: { width: 1920, height: 1080, label: 'TV (1920×1080)' },
  phone: { width: 390, height: 844, label: 'Phone (390×844)' },
};

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
const round20 = (v: number): number => Math.round(v / 20) * 20;

/** Clamp + grid-snap a (possibly AI-produced, partial) zone into surface
 *  bounds and assign a fresh id. Renderer-side sanitisation (TDD §7.2). */
export function sanitizeZone(raw: Partial<Zone>, surface: ScreenSurface, id: string): Zone {
  const dims = SURFACE_DIMENSIONS[surface];
  const x = clamp(round20(raw.x ?? 0), 0, dims.width - 40);
  const y = clamp(round20(raw.y ?? 0), 0, dims.height - 40);
  const width = clamp(round20(raw.width ?? 100), 40, dims.width - x);
  const height = clamp(round20(raw.height ?? 60), 40, dims.height - y);
  return {
    id,
    x,
    y,
    width,
    height,
    label: (raw.label ?? '').slice(0, 80),
    kind: ZONE_KINDS.find((k) => k.value === raw.kind)?.value ?? 'other',
    notes: (raw.notes ?? '').slice(0, 400),
    ...(typeof raw.zIndex === 'number' ? { zIndex: raw.zIndex } : {}),
  };
}
